/**
 * Flow Studio Application
 *
 * Auth wiring matches app-template's / saas-tenant-administration's / playground-app's SSO
 * token-handoff pattern exactly (see those apps' App.tsx for the canonical version this was
 * ported from) — all login happens through the real login app; no BizFirst app renders its own
 * login form. This app previously ran "without authentication" (auth was a stub — see git
 * history); @flow-studio/store's own local useAuthStore existed but nothing ever called
 * setUser()/clearUser(), so `user` was permanently null there.
 *
 * Flow Studio has no client-side router for its own views — WorkflowApp drives an internal
 * AppState union instead (dashboard/designer/settings/apps/observer/hil-test — see
 * WorkflowApp.tsx), not routes. The two @passport/pages routes below (/profile,
 * /change-password) are the only real react-router-dom routes in this app; every other path
 * falls through the catch-all "/*" straight into WorkflowApp, unchanged.
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, TenantBrandingAssets } from '@bizfirst/common-react';
import { SsoAuthGate } from '@bizfirst/common-auth-react';
import { ProfilePage, ChangePasswordPage } from '@passport/pages';
import { WorkflowApp } from 'flow-studio-designer';
import { AtlasFormsClient } from '@atlas-forms/client-js';
import { useAuthStore } from '@passport/store';
import { calculateApiUrl } from '@bizfirst/common-js';
import { registerAllControls } from '@atlas-forms/player-components-react';
import './App.css';

/**
 * Validates and returns configuration from environment variables.
 * VITE_API_BASE_URL is optional — calculateApiUrl() derives a same-origin
 * default when it's unset, so only VITE_TENANT_ID fails fast here.
 */
function getConfig() {
  const apiBaseUrl = calculateApiUrl(import.meta.env.VITE_API_BASE_URL);
  const tenantIdStr = import.meta.env.VITE_TENANT_ID;

  const tenantId = parseInt(tenantIdStr ?? '1', 10);
  if (isNaN(tenantId) || tenantId <= 0) {
    throw new Error(
      'Invalid VITE_TENANT_ID: must be a positive number\n' +
      `Received: "${tenantIdStr}"`
    );
  }

  if (!import.meta.env.DEV) {
    console.log('[Config] API Base URL:', apiBaseUrl);
    console.log('[Config] Tenant ID:', tenantId);
  }

  return { apiBaseUrl, tenantId };
}

// Computed once at module-load time — but a bad VITE_TENANT_ID must not throw here: a throw
// during module evaluation aborts before React ever mounts, leaving the user with a blank
// white page and the only clue in the console. Captured as a result instead, so App() can
// render a visible error screen.
const configResult = (() => {
  try {
    const config = getConfig();
    return { ok: true as const, apiBaseUrl: config.apiBaseUrl, tenantId: config.tenantId };
  } catch (error) {
    console.error('Configuration error:', error);
    return { ok: false as const, error };
  }
})();

/** Shown when configuration is invalid. Details go to the console; only dev builds show them on screen. */
function ConfigurationErrorScreen({ error }: { error: unknown }) {
  const detail = import.meta.env.DEV ? String(error) : null;
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        fontFamily: 'system-ui, sans-serif',
        background: '#f5f5f5',
        color: '#333',
      }}
    >
      <h1 style={{ fontSize: 20, margin: 0 }}>Flow Studio is not configured correctly</h1>
      <p style={{ marginTop: 8 }}>Please contact your administrator.</p>
      {detail && <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{detail}</pre>}
    </div>
  );
}

/**
 * Main App component. Validated configuration is passed down as props so no placeholder
 * defaults (empty base URL / tenant 0) ever exist for a reader to pick up by mistake.
 */
export function App() {
  if (!configResult.ok) {
    return <ConfigurationErrorScreen error={configResult.error} />;
  }
  return <ConfiguredApp apiBaseUrl={configResult.apiBaseUrl} tenantId={configResult.tenantId} />;
}

function ConfiguredApp({ apiBaseUrl: API_BASE_URL, tenantId: TENANT_ID }: { apiBaseUrl: string; tenantId: number }) {
  // Atlas Forms client + control registration — unrelated to auth. Must keep running on
  // every load regardless of session state so the designer's form-picker/form-renderer
  // controls are registered before any workflow can use them.
  // The Atlas Forms API calls (form picker's forms/list, etc.) need the signed-in access token.
  // The SSO handoff stores it in @passport/store; re-initialise the client whenever it changes,
  // the same way Atlas Form Studio does. Initialising with an empty token sent requests with no
  // Authorization header, which the API rejects with 401.
  const accessToken = useAuthStore((s) => s.token);

  useEffect(() => {
    AtlasFormsClient.initialize({
      apiBaseUrl: API_BASE_URL,
      tenantId: TENANT_ID,
      token: accessToken ?? '',
    });
  }, [API_BASE_URL, TENANT_ID, accessToken]);

  useEffect(() => {
    console.log('[App] Registering Atlas Forms controls...');
    registerAllControls();
    console.log('[App] All controls registered successfully');
  }, []);

  return (
    <ThemeProvider defaultTheme="dark">
      <TenantBrandingAssets apiBaseUrl={API_BASE_URL} />
      {/* SsoAuthGate owns the whole session-check/redirect-branch that used to be ~90 lines
          here (existing-session verify, exchange-code redemption, loading state, login
          redirect) — see its own doc comment. mirrorLegacyAuthTokenKey="authToken": a handful
          of this app's own API clients (NodeTemplateApiClient, flow-studio-api's
          baseApiClient, and others — see WorkflowApp.tsx's own HIL cross-origin token handoff
          for the established convention) predate the SSO migration and still read that plain
          localStorage key directly rather than @passport/store's Zustand-managed session. */}
      <SsoAuthGate appName="Flow Studio" apiBaseUrl={API_BASE_URL} mirrorLegacyAuthTokenKey="authToken">
        {() => (
          <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '')}>
            <Routes>
              {/* @passport/pages' shared Profile/Change-Password pages — reached via
                  AuthHeaderControls' "View User Profile" row (AppLayout.tsx) and ProfilePage's
                  own "Change Password" link, respectively. Real, dedicated routes (not folded
                  into WorkflowApp's internal AppState) since WorkflowApp has no concept of
                  either page — same convention app-template/saas-tenant-administration use. */}
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              {/* Everything else — the entire dashboard/designer/settings/apps/observer/HIL-
                  test surface — is still driven by WorkflowApp's own internal AppState,
                  completely untouched by this migration. */}
              <Route path="/*" element={<WorkflowApp />} />
            </Routes>
          </BrowserRouter>
        )}
      </SsoAuthGate>
    </ThemeProvider>
  );
}
